import React, { useState } from 'react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { loginUser, fetchCurrentUser } from '../api';
import { API_URL } from '../config';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setShowResendVerification(false);
    try {
      const data = await loginUser(email, password);
      const userObj = await fetchCurrentUser(data.access_token);
      login(userObj, data.access_token);
      navigate('/dashboard');
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Login failed';
      setError(errorMessage);
      // Check if it's an email verification error
      if (err.response?.status === 403 && errorMessage.includes('Email not verified')) {
        setShowResendVerification(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setLoading(true);
    setResendSuccess(false);
    try {
      await fetch(`${API_URL}/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });
      setResendSuccess(true);
      setError('');
    } catch (err) {
      setError('Failed to resend verification email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xs" sx={{ mt: 8 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          mb: 4,
        }}
      >
        <Typography
          variant="h4"
          align="center"
          sx={{
            fontWeight: 700,
            letterSpacing: 1,
            textTransform: 'none',
          }}
        >
          TackleTribe.
        </Typography>
        <Typography variant="subtitle2" align="center" color="text.secondary">
          Deine Crew. Deine Faenge. Dein Tackle.
        </Typography>
        <Typography variant="h6" align="center" sx={{ mt: 1 }}>
          Welcome back!
        </Typography>
      </Box>
      <Typography variant="h5" align="center" gutterBottom>
        Login
      </Typography>
      <Box component="form" onSubmit={handleLogin}>
        <TextField
          label="Email"
          type="email"
          fullWidth
          margin="normal"
          value={email}
          onChange={e => setEmail(e.target.value)}
          required
        />
        <TextField
          label="Password"
          type="password"
          fullWidth
          margin="normal"
          value={password}
          onChange={e => setPassword(e.target.value)}
          required
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          disabled={loading}
          sx={{ mt: 2 }}
        >
          {loading ? 'Logging in...' : 'Login'}
        </Button>
        {showResendVerification && (
          <Button
            fullWidth
            variant="outlined"
            color="secondary"
            onClick={handleResendVerification}
            disabled={loading || resendSuccess}
            sx={{ mt: 2 }}
          >
            {resendSuccess ? 'Verification Email Sent!' : 'Resend Verification Email'}
          </Button>
        )}
      </Box>
      
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Don't have an account?{' '}
          <Button
            onClick={() => navigate('/register')}
            sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
          >
            Sign up
          </Button>
        </Typography>
      </Box>

      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')}>
        <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
      </Snackbar>
      <Snackbar open={resendSuccess} autoHideDuration={6000} onClose={() => setResendSuccess(false)}>
        <Alert severity="success" onClose={() => setResendSuccess(false)}>
          Verification email sent! Please check your inbox.
        </Alert>
      </Snackbar>
    </Container>
  );
}
